import { useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { setSelectedCommunity } from "../store/slices/communitySlice";
import {
  useGetAccessibleCommunitiesQuery,
  AccessibleCommunity,
} from "../store/api/crmCommunitiesApi";

/**
 * App-wide (sales-side) selected-community accessor + reconciler. Mirrors useTenantInstance:
 * reads the global community slice and dispatches changes, so the selection survives navigation.
 *
 * Also runs the stale-reconcile that CommunitySelector used to own (CommunitySelector.tsx:66-78):
 * when the accessible-communities set changes (e.g. an instance switch), a now-invalid selection
 * drops to single-or-null, and a single-community instance auto-selects. The reconcile runs only
 * while a consumer is MOUNTED, so until the header control mounts (a later round) this hook has
 * NO effect on any surface.
 */

/**
 * ⚠️ THE CHOICE SURVIVES A RELOAD — it did not, and that was a real complaint.
 *
 * The slice's own note said "Not persisted across full reload (a later
 * decision)". This is that decision: refreshing any page dropped the pill back
 * to "All communities", so a manager who works one community re-picked it every
 * time the tab reloaded, and every list silently widened to the whole instance
 * in between.
 *
 * Keyed BY INSTANCE, because a community id means nothing under a different
 * instance and restoring one across a switch would select a community the
 * operator cannot see. The reconcile below is still the backstop: a restored id
 * that is not in the accessible set is dropped exactly like any other stale one.
 *
 * "All communities" is stored explicitly rather than as an absent key. It is a
 * DELIBERATE choice, and treating it as "nothing saved" would let a later
 * auto-select quietly overrule it.
 */
const STORAGE_PREFIX = "skycrm.selectedCommunity.";
const ALL_COMMUNITIES = "__all__";

function storageKey(instanceId: string): string {
  return `${STORAGE_PREFIX}${instanceId}`;
}

function readPersisted(instanceId: string | undefined): string | null | undefined {
  if (!instanceId) return undefined;
  try {
    const raw = window.localStorage.getItem(storageKey(instanceId));
    if (raw === null) return undefined; // nothing saved — not the same as "All"
    return raw === ALL_COMMUNITIES ? null : raw;
  } catch {
    // Private mode / storage disabled: behave exactly as before persistence.
    return undefined;
  }
}

function writePersisted(instanceId: string | undefined, id: string | null): void {
  if (!instanceId) return;
  try {
    window.localStorage.setItem(storageKey(instanceId), id ?? ALL_COMMUNITIES);
  } catch {
    /* storage unavailable — selection still works for this session */
  }
}

export function useSelectedCommunity(instanceId: string | undefined): {
  selectedCommunityId: string | null;
  setSelectedCommunity: (id: string | null) => void;
  communities: AccessibleCommunity[];
  isLoading: boolean;
} {
  const dispatch = useDispatch();
  const selectedCommunityId = useSelector((s: RootState) => s.communityReducer.selectedCommunityId);
  const { data, isLoading } = useGetAccessibleCommunitiesQuery(
    { instanceId: instanceId ?? "" },
    { skip: !instanceId },
  );
  const communities = data?.communities ?? [];

  const set = useCallback(
    (id: string | null) => {
      // Writing "All" on a single-community instance stores a value the
      // reconcile will always overrule — which is precisely the value that
      // poisoned localStorage and made the crash survive reloads.
      if (!(id === null && communities.length === 1)) writePersisted(instanceId, id);
      dispatch(setSelectedCommunity(id));
    },
    [dispatch, instanceId, communities.length],
  );

  /**
   * Rehydrate once the accessible set is known.
   *
   * ⚠️ TWO EFFECTS USED TO FIGHT HERE, AND IT CRASHED THE APP. The reconcile
   * below owns one invariant: with exactly ONE accessible community, the
   * selection is always that community. An operator on a single-community
   * instance who picked "All communities" therefore produced a stored `null`
   * that the reconcile immediately overwrote — and this effect, seeing storage
   * disagree with state, wrote it back. Dispatch → reconcile → dispatch, until
   * React threw "Maximum update depth exceeded" and GlobalCommunitySelector
   * unmounted into a blank page. It SURVIVED RELOADS, because the offending
   * value was in localStorage: the operator could not click their way out.
   *
   * Two guards, deliberately both:
   *   1. Never restore "All" on a single-community instance. There, "All" and
   *      "that community" describe the same set, so the reconcile is right and
   *      this has nothing to add — it just steps aside.
   *   2. Restore AT MOST ONCE per instance. Even if some future rule disagrees
   *      with a stored value, a one-shot restore can be overruled without a
   *      loop. A rehydrate that re-asserts itself every render is a fight
   *      waiting for a second opinion.
   */
  const rehydratedForInstance = useRef<string | null>(null);
  useEffect(() => {
    if (!instanceId || communities.length === 0) return;
    if (rehydratedForInstance.current === instanceId) return; // one shot (guard 2)
    const persisted = readPersisted(instanceId);
    if (persisted === undefined) {
      rehydratedForInstance.current = instanceId;
      return; // nothing saved for this instance
    }
    // guard 1 — the single-community invariant belongs to the reconcile
    if (persisted === null && communities.length === 1) {
      rehydratedForInstance.current = instanceId;
      return;
    }
    if (persisted !== null && !communities.some((c) => c.id === persisted)) {
      rehydratedForInstance.current = instanceId;
      return; // stale; the reconcile handles it
    }
    rehydratedForInstance.current = instanceId;
    if (persisted !== selectedCommunityId) dispatch(setSelectedCommunity(persisted));
  }, [instanceId, communities, selectedCommunityId, dispatch]);

  useEffect(() => {
    if (communities.length === 0) return; // wait for data; don't clobber during load
    const known =
      selectedCommunityId != null && communities.some((c) => c.id === selectedCommunityId);
    if (selectedCommunityId != null && !known) {
      // stale selection (e.g. carried across an instance switch) -> reconcile
      dispatch(setSelectedCommunity(communities.length === 1 ? communities[0].id : null));
      return;
    }
    if (selectedCommunityId == null && communities.length === 1) {
      dispatch(setSelectedCommunity(communities[0].id));
    }
  }, [selectedCommunityId, communities, dispatch]);

  return { selectedCommunityId, setSelectedCommunity: set, communities, isLoading };
}
